import React, { useState, useEffect } from 'react';
import axiosInstance from '../../../helpers/axios';

// Full month names for the payout table title (month from API is 1-indexed)
const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

// Per-file buckets — their incentives are summed into one "Per File" column in the payout table
const PER_FILE_BUCKETS = ['M2', 'X1', 'X2', 'WO'];

// Shared style for the small card inputs inside the edit form
const editCardStyle = {
  background: '#fafbfc',
  borderRadius: '6px',
  padding: '10px',
  border: '1px solid #eee',
};

const smallLabelStyle = {
  fontSize: '10px',
  color: '#667085',
  display: 'block',
  marginBottom: '2px',
};

const smallInputStyle = {
  width: '100%',
  padding: '5px',
  border: '1px solid #ddd',
  borderRadius: '4px',
  fontSize: '12px',
  marginBottom: '6px',
  boxSizing: 'border-box',
};

// Generate last 12 months options for the month picker
function getMonthOptions() {
  const options = [];
  const now = new Date();
  for (let i = 0; i < 12; i++) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    options.push({ month: d.getMonth() + 1, year: d.getFullYear(), label: `${MONTHS[d.getMonth()]} ${d.getFullYear()}` });
  }
  return options;
}

export default function ManagerIncentives({ initialData }) {
  const [rules, setRules]               = useState(null);
  const [payoutReport, setPayoutReport] = useState(initialData?.report ?? null);
  const [loading, setLoading]           = useState(true);
  const [reportLoading, setReportLoading] = useState(false);

  // Default to previous month so manager sees completed month on salary day
  const now = new Date();
  const defaultMonth = now.getMonth() === 0 ? 12 : now.getMonth();
  const defaultYear = now.getMonth() === 0 ? now.getFullYear() - 1 : now.getFullYear();
  const [selectedMonth, setSelectedMonth] = useState(defaultMonth);
  const [selectedYear, setSelectedYear] = useState(defaultYear);

  const monthOptions = getMonthOptions();

  // Controls whether the inline edit form is visible instead of the view table
  const [editMode, setEditMode] = useState(false);

  // Edit form inputs use plain percentages (e.g. 90, 75) — not decimals (0.9, 0.75)
  // Pre-filled from API on load; converted back to decimals when saving (divided by 100)
  const [editForm, setEditForm] = useState({
    s1Threshold: '', s1Amount: '',
    s2Threshold: '', s2Amount: '',
    s3Threshold: '', s3Amount: '',
    m1Threshold: '', m1Amount: '',
    m2PosThreshold: '', m2BelowThreshold: '', m2AtOrAboveThreshold: '',
    x1Rate: '', x2Rate: '', woRate: '',
  });

  // Load settings once on mount
  useEffect(() => {
    axiosInstance.get('/collection-incentive/settings')
      .then((settingsRes) => {
        const incentiveRules = settingsRes.data.data.incentiveRules;
        setRules(incentiveRules);
        const tb = incentiveRules.targetBased;
        const pf = incentiveRules.perFile;
        setEditForm({
          s1Threshold: Math.round(tb.S1.threshold * 100),
          s1Amount: tb.S1.amount,
          s2Threshold: Math.round(tb.S2.threshold * 100),
          s2Amount: tb.S2.amount,
          s3Threshold: Math.round(tb.S3.threshold * 100),
          s3Amount: tb.S3.amount,
          m1Threshold: Math.round(tb.M1.threshold * 100),
          m1Amount: tb.M1.amount,
          m2PosThreshold: pf.M2.posThreshold,
          m2BelowThreshold: pf.M2.belowThreshold,
          m2AtOrAboveThreshold: pf.M2.atOrAboveThreshold,
          x1Rate: Math.round(pf.X1.rate * 100),
          x2Rate: Math.round(pf.X2.rate * 100),
          woRate: Math.round(pf.WO.rate * 100),
        });
      })
      .catch((error) => {
        alert(error?.response?.data?.message || 'Failed to load incentive rules');
      })
      .finally(() => setLoading(false));
  }, []);

  // Sync prefetched report when parent refreshes
  useEffect(() => {
    if (!initialData?.report) return;
    setPayoutReport(initialData.report);
  }, [initialData]);

  // Reload payout report whenever selected month/year changes
  useEffect(() => {
    setReportLoading(true);
    axiosInstance.get(`/collection-incentive/manager/incentives/report?month=${selectedMonth}&year=${selectedYear}`)
      .then((res) => setPayoutReport(res.data.data))
      .catch((error) => {
        alert(error?.response?.data?.message || 'Failed to load payout report');
      })
      .finally(() => setReportLoading(false));
  }, [selectedMonth, selectedYear]);

  const handleSaveRules = () => {
    // Divide percentage inputs by 100 to convert back to decimals before sending to backend
    axiosInstance
      .put(
        '/collection-incentive/settings/incentive-rules',
        {
          s1: { threshold: Number(editForm.s1Threshold) / 100, amount: Number(editForm.s1Amount) },
          s2: { threshold: Number(editForm.s2Threshold) / 100, amount: Number(editForm.s2Amount) },
          s3: { threshold: Number(editForm.s3Threshold) / 100, amount: Number(editForm.s3Amount) },
          m1: { threshold: Number(editForm.m1Threshold) / 100, amount: Number(editForm.m1Amount) },
          m2PosThreshold: Number(editForm.m2PosThreshold),
          m2BelowThreshold: Number(editForm.m2BelowThreshold),
          m2AtOrAboveThreshold: Number(editForm.m2AtOrAboveThreshold),
          x1Rate: Number(editForm.x1Rate) / 100,
          x2Rate: Number(editForm.x2Rate) / 100,
          woRate: Number(editForm.woRate) / 100,
        }
      )
      .then(() => {
        alert('Incentive rules saved. Changes apply from next calendar month.');
        setEditMode(false);
      })
      .catch((error) => {
        alert(error?.response?.data?.message || 'Failed to update rules');
      });
  };

  if (loading) {
    return <div style={{ padding: '20px', color: '#667085' }}>Loading...</div>;
  }

  const payoutMonthLabel = `${MONTHS[selectedMonth - 1]} ${selectedYear}`;

  return (
    <div style={{ padding: '10px' }}>

      {/* ── Incentive Structure table ── */}
      <div style={{
        border: '1px solid #eee',
        borderRadius: '8px',
        overflow: 'hidden',
        marginBottom: '18px',
        backgroundColor: 'white',
      }}>

        {/* Header row with title and Edit Rules button */}
        <div style={{ padding: '12px 16px', borderBottom: '1px solid #eee', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <p style={{ margin: 0, fontFamily: 'Inter-Medium', fontSize: '14px', fontWeight: '600', color: '#344054' }}>
            Incentive Structure
          </p>
          {!editMode && (
            <button
              onClick={() => setEditMode(true)}
              style={{
                padding: '5px 12px',
                backgroundColor: 'white',
                color: '#d32028',
                border: '1px solid #d32028',
                borderRadius: '5px',
                fontFamily: 'Inter-Medium',
                fontSize: '12px',
                fontWeight: '600',
                cursor: 'pointer',
              }}
            >
              ✏️ Edit Rules
            </button>
          )}
        </div>

        {/* Read-only view table — hidden when edit form is open */}
        {!editMode && rules && (
          <table style={{ width: '100%', borderCollapse: 'collapse', fontFamily: 'Inter-Medium', fontSize: '12px' }}>
            <thead>
              <tr style={{ backgroundColor: '#fafbfc' }}>
                {['Bucket', 'Target', 'Incentive', 'Type'].map((col) => (
                  <th key={col} style={{ padding: '8px 12px', textAlign: 'left', color: '#667085', fontWeight: '600', borderBottom: '1px solid #eee', fontSize: '11px' }}>
                    {col}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {/* S1 — shows "90% recovery" to distinguish it from a plain percentage target */}
              <tr style={{ borderBottom: '1px solid #f5f5f5' }}>
                <td style={{ padding: '8px 12px', color: '#344054' }}>S1</td>
                <td style={{ padding: '8px 12px', color: '#344054' }}>{Math.round(rules.targetBased.S1.threshold * 100)}% recovery</td>
                <td style={{ padding: '8px 12px', color: '#2e7d32', fontWeight: '700' }}>₹{rules.targetBased.S1.amount.toLocaleString('en-IN')}</td>
                <td style={{ padding: '8px 12px', color: '#344054' }}>Flat</td>
              </tr>
              {/* S2, S3, M1 — plain percentage target */}
              {['S2', 'S3', 'M1'].map((bucket) => (
                <tr key={bucket} style={{ borderBottom: '1px solid #f5f5f5' }}>
                  <td style={{ padding: '8px 12px', color: '#344054' }}>{bucket}</td>
                  <td style={{ padding: '8px 12px', color: '#344054' }}>{Math.round(rules.targetBased[bucket].threshold * 100)}%</td>
                  <td style={{ padding: '8px 12px', color: '#2e7d32', fontWeight: '700' }}>₹{rules.targetBased[bucket].amount.toLocaleString('en-IN')}</td>
                  <td style={{ padding: '8px 12px', color: '#344054' }}>Flat</td>
                </tr>
              ))}
              {/* M2 — fixed amount based on POS size */}
              <tr style={{ borderBottom: '1px solid #f5f5f5' }}>
                <td style={{ padding: '8px 12px', color: '#344054' }}>M2</td>
                <td style={{ padding: '8px 12px', color: '#344054' }}>Per file</td>
                <td style={{ padding: '8px 12px', color: '#2e7d32', fontWeight: '700' }}>
                  ₹{rules.perFile.M2.belowThreshold}/₹{rules.perFile.M2.atOrAboveThreshold >= 1000
                    ? (rules.perFile.M2.atOrAboveThreshold / 1000) + 'K'
                    : rules.perFile.M2.atOrAboveThreshold}
                </td>
                <td style={{ padding: '8px 12px', color: '#344054' }}>POS based</td>
              </tr>
              {/* X1 — percentage of recovered amount */}
              <tr style={{ borderBottom: '1px solid #f5f5f5' }}>
                <td style={{ padding: '8px 12px', color: '#344054' }}>X1</td>
                <td style={{ padding: '8px 12px', color: '#344054' }}>Per file</td>
                <td style={{ padding: '8px 12px', color: '#2e7d32', fontWeight: '700' }}>{Math.round(rules.perFile.X1.rate * 100)}% POS</td>
                <td style={{ padding: '8px 12px', color: '#344054' }}>%</td>
              </tr>
              {/* X2/WO — same percentage rate, shown as one combined row */}
              <tr>
                <td style={{ padding: '8px 12px', color: '#344054' }}>X2/WO</td>
                <td style={{ padding: '8px 12px', color: '#344054' }}>Per file</td>
                <td style={{ padding: '8px 12px', color: '#2e7d32', fontWeight: '700' }}>{Math.round(rules.perFile.X2.rate * 100)}% POS</td>
                <td style={{ padding: '8px 12px', color: '#344054' }}>%</td>
              </tr>
            </tbody>
          </table>
        )}

        {/* Inline edit form — shown when Edit Rules is clicked */}
        {editMode && (
          <div style={{ padding: '16px' }}>

            <p style={{ margin: '0 0 10px', fontFamily: 'Inter-Medium', fontSize: '12px', fontWeight: '600', color: '#344054' }}>
              Target-Based Incentives (S1→M1)
            </p>

            {/* 4-card grid for target-based buckets — each card has Target % and Amount ₹ inputs */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '10px', marginBottom: '16px' }}>
              {[
                { label: 'S1', thresholdKey: 's1Threshold', amountKey: 's1Amount' },
                { label: 'S2', thresholdKey: 's2Threshold', amountKey: 's2Amount' },
                { label: 'S3', thresholdKey: 's3Threshold', amountKey: 's3Amount' },
                { label: 'M1', thresholdKey: 'm1Threshold', amountKey: 'm1Amount' },
              ].map((card) => (
                <div key={card.label} style={editCardStyle}>
                  <p style={{ margin: '0 0 6px', fontSize: '11px', fontWeight: '600', color: '#344054' }}>{card.label}</p>
                  <label style={smallLabelStyle}>Target %</label>
                  <input
                    type="number"
                    min={0}
                    max={100}
                    value={editForm[card.thresholdKey]}
                    onChange={(e) => setEditForm({ ...editForm, [card.thresholdKey]: e.target.value })}
                    style={smallInputStyle}
                  />
                  <label style={smallLabelStyle}>Amount ₹</label>
                  <input
                    type="number"
                    min={0}
                    step={100}
                    value={editForm[card.amountKey]}
                    onChange={(e) => setEditForm({ ...editForm, [card.amountKey]: e.target.value })}
                    style={{ ...smallInputStyle, marginBottom: 0 }}
                  />
                </div>
              ))}
            </div>

            <p style={{ margin: '0 0 10px', fontFamily: 'Inter-Medium', fontSize: '12px', fontWeight: '600', color: '#344054' }}>
              Per-File Incentives (M2→WO)
            </p>

            {/* 4-card grid for per-file buckets — M2 has 3 fields, X1/X2/WO each have 1 field */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '10px', marginBottom: '16px' }}>

              {/* M2 — three amount fields depending on POS size */}
              <div style={editCardStyle}>
                <p style={{ margin: '0 0 6px', fontSize: '11px', fontWeight: '600', color: '#344054' }}>M2</p>
                <label style={smallLabelStyle}>POS Threshold ₹</label>
                <input type="number" min={0} step={5000} value={editForm.m2PosThreshold} onChange={(e) => setEditForm({ ...editForm, m2PosThreshold: e.target.value })} style={smallInputStyle} />
                <label style={smallLabelStyle}>Below threshold ₹</label>
                <input type="number" min={0} step={50} value={editForm.m2BelowThreshold} onChange={(e) => setEditForm({ ...editForm, m2BelowThreshold: e.target.value })} style={smallInputStyle} />
                <label style={smallLabelStyle}>At/Above threshold ₹</label>
                <input type="number" min={0} step={50} value={editForm.m2AtOrAboveThreshold} onChange={(e) => setEditForm({ ...editForm, m2AtOrAboveThreshold: e.target.value })} style={{ ...smallInputStyle, marginBottom: 0 }} />
              </div>

              {/* X1, X2, WO — single percentage input each */}
              {[
                { label: 'X1', key: 'x1Rate' },
                { label: 'X2', key: 'x2Rate' },
                { label: 'WO', key: 'woRate' },
              ].map((card) => (
                <div key={card.label} style={editCardStyle}>
                  <p style={{ margin: '0 0 6px', fontSize: '11px', fontWeight: '600', color: '#344054' }}>{card.label}</p>
                  <label style={smallLabelStyle}>% of recovered</label>
                  <input
                    type="number"
                    min={1}
                    max={50}
                    value={editForm[card.key]}
                    onChange={(e) => setEditForm({ ...editForm, [card.key]: e.target.value })}
                    style={{ ...smallInputStyle, marginBottom: 0 }}
                  />
                </div>
              ))}
            </div>

            {/* Save + Cancel buttons with note */}
            <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
              <button
                onClick={handleSaveRules}
                style={{
                  padding: '7px 14px',
                  backgroundColor: '#d32028',
                  color: 'white',
                  border: 'none',
                  borderRadius: '5px',
                  fontFamily: 'Inter-Medium',
                  fontSize: '12px',
                  fontWeight: '600',
                  cursor: 'pointer',
                }}
              >
                Save Incentive Rules
              </button>
              <button
                onClick={() => setEditMode(false)}
                style={{
                  padding: '7px 14px',
                  backgroundColor: 'white',
                  color: '#344054',
                  border: '1px solid #d0d5dd',
                  borderRadius: '5px',
                  fontFamily: 'Inter-Medium',
                  fontSize: '12px',
                  cursor: 'pointer',
                }}
              >
                Cancel
              </button>
              <span style={{ fontSize: '11px', color: '#888' }}>Changes apply from next calendar month</span>
            </div>

          </div>
        )}
      </div>

      {/* ── Agent Payouts table ── */}
      <div style={{
        border: '1px solid #eee',
        borderRadius: '8px',
        overflow: 'hidden',
        backgroundColor: 'white',
      }}>
        <div style={{ padding: '12px 16px', borderBottom: '1px solid #eee', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '8px' }}>
          <p style={{ margin: 0, fontFamily: 'Inter-Medium', fontSize: '14px', fontWeight: '600', color: '#344054' }}>
            Agent Payouts — {payoutMonthLabel}
          </p>
          {/* Month picker — defaults to previous month; salary is processed on 8th of each month */}
          <select
            value={`${selectedMonth}-${selectedYear}`}
            onChange={(e) => {
              const [m, y] = e.target.value.split('-');
              setSelectedMonth(Number(m));
              setSelectedYear(Number(y));
            }}
            style={{
              padding: '5px 10px',
              border: '1px solid #d0d5dd',
              borderRadius: '6px',
              fontSize: '12px',
              fontFamily: 'Inter-Medium',
              color: '#344054',
              backgroundColor: 'white',
              cursor: 'pointer',
            }}
          >
            {monthOptions.map((opt) => (
              <option key={`${opt.month}-${opt.year}`} value={`${opt.month}-${opt.year}`}>
                {opt.label}
              </option>
            ))}
          </select>
        </div>

        <div style={{ overflowX: 'auto' }}>
          {reportLoading ? (
            <div style={{ padding: '24px', textAlign: 'center', color: '#667085', fontSize: '13px' }}>Loading...</div>
          ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse', fontFamily: 'Inter-Medium', fontSize: '12px' }}>
            <thead>
              <tr style={{ backgroundColor: '#fafbfc' }}>
                {['Agent', 'S1', 'S2', 'S3', 'M1', 'Per File', 'Total'].map((col) => (
                  <th key={col} style={{ padding: '8px 12px', textAlign: 'left', color: '#667085', fontWeight: '600', borderBottom: '1px solid #eee', fontSize: '11px', whiteSpace: 'nowrap' }}>
                    {col}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {payoutReport && payoutReport.agentReports && payoutReport.agentReports.length > 0 ? (
                payoutReport.agentReports.map((agent, index) => {
                  // Sum M2 + X1 + X2 + WO incentives into the single "Per File" column
                  const perFileTotal = PER_FILE_BUCKETS.reduce((sum, bucket) => {
                    return sum + (agent.bucketBreakdown?.[bucket]?.totalIncentive ?? 0);
                  }, 0);

                  return (
                    <tr key={agent.agentId || index} style={{ borderBottom: '1px solid #f5f5f5' }}>
                      <td style={{ padding: '8px 12px', fontWeight: '600', color: '#344054' }}>
                        {/* Show agent name — falls back to agentId if name lookup failed */}
                        {agent.agentName || agent.agentId}
                      </td>
                      {/* Fixed target-based columns S1, S2, S3, M1 */}
                      {['S1', 'S2', 'S3', 'M1'].map((bucket) => (
                        <td key={bucket} style={{ padding: '8px 12px', color: '#344054' }}>
                          {agent.bucketBreakdown?.[bucket]?.totalIncentive != null
                            ? `₹${Number(agent.bucketBreakdown[bucket].totalIncentive).toLocaleString('en-IN')}`
                            : '—'}
                        </td>
                      ))}
                      {/* Per File = M2 + X1 + X2 + WO combined */}
                      <td style={{ padding: '8px 12px', color: '#344054' }}>
                        {perFileTotal > 0 ? `₹${perFileTotal.toLocaleString('en-IN')}` : '—'}
                      </td>
                      {/* Total in green */}
                      <td style={{ padding: '8px 12px', color: '#2e7d32', fontWeight: '700' }}>
                        ₹{Number(agent.totalIncentive).toLocaleString('en-IN')}
                      </td>
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td colSpan={7} style={{ padding: '24px', textAlign: 'center', color: '#667085' }}>
                    No payout data for this month
                  </td>
                </tr>
              )}
            </tbody>
          </table>
          )}
        </div>
      </div>

    </div>
  );
}
